import { useEffect, useMemo, useRef, useState } from "react";
import { Archive, ArchiveRestore, CheckCircle2, MoreVertical, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../../app/useAuth";
import { LoadingState } from "../../../components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { ErrorState } from "../../../components/ui/error-state";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useQuestingGroups } from "../../../hooks/useQuestingGroups";
import { useUserSettings } from "../../../hooks/useUserSettings";
import { useUserProfilesByIds } from "../../../hooks/useUserProfiles";
import { BasicPollVotingCard } from "../../../components/polls/basic-poll-voting-card";
import { PollDiscordMetaRow } from "../../../components/polls/poll-discord-meta-row";
import { PollNudgeButton } from "../../../components/polls/poll-nudge-button";
import { PollOptionNoteDialog } from "../../../components/polls/poll-option-note-dialog";
import { ConfirmDialog } from "../../../components/ui/confirm-dialog";
import {
  hasSubmittedVoteForPoll,
  normalizeVoteOptionIds,
  normalizeVoteRankings,
} from "../../../lib/basic-polls/vote-submission";
import {
  addRankedOptionToVoteDraft,
  moveRankedOptionInVoteDraft,
  removeRankedOptionFromVoteDraft,
  setMultipleChoiceOptionOnVoteDraft,
  setOtherTextOnVoteDraft,
} from "../../../lib/basic-polls/vote-draft";
import {
  BASIC_POLL_STATUSES,
  BASIC_POLL_VOTE_TYPES,
  MAX_WRITE_IN_LENGTH,
} from "../../../lib/basic-polls/constants";
import { resolveBasicPollDeadline } from "../../../lib/voting-deadlines";
import { getVoteTally } from "../../../lib/vote-tally";
import {
  canViewOtherVotesForUser,
  getVoteIdentityDisplayMode,
  resolveVoteVisibility,
} from "../../../lib/vote-visibility";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../../../lib/anonymous-voter-identities";
import {
  deleteBasicPoll,
  deleteBasicPollVote,
  finalizeBasicPoll,
  reopenBasicPoll,
  submitBasicPollVote,
  subscribeToBasicPoll,
  subscribeToBasicPollVotes,
  subscribeToMyBasicPollVote,
} from "../../../lib/data/basicPolls";
import { nudgeDiscordBasicPoll } from "../../../lib/data/discord";

export function GroupBasicPollModal({ groupId, pollId, onClose, onEditPoll, onPollDeleted }) {
  const { user } = useAuth();
  const { groups, loading: groupsLoading } = useQuestingGroups();
  const { archivedPolls, archivePoll, unarchivePoll } = useUserSettings();
  const [poll, setPoll] = useState(null);
  const [pollLoading, setPollLoading] = useState(true);
  const [pollError, setPollError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const [votes, setVotes] = useState([]);
  const [votesLoading, setVotesLoading] = useState(true);
  const [votesError, setVotesError] = useState(null);
  const [myVote, setMyVote] = useState(null);
  const [voteDraft, setVoteDraft] = useState({});
  const [submittingVote, setSubmittingVote] = useState(false);
  const [clearingVote, setClearingVote] = useState(false);
  const [voteError, setVoteError] = useState(null);
  const [optionNoteViewer, setOptionNoteViewer] = useState(null);
  const [headerActionBusy, setHeaderActionBusy] = useState(false);
  const [nudgeSending, setNudgeSending] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Dirty flag: once the user starts editing the draft, realtime poll-metadata
  // updates must NOT overwrite the in-progress edit. Cleared after the user's
  // own submit/clear (so the fresh myVote can re-sync) and on poll switch.
  const draftDirtyRef = useRef(false);
  // Bump to force a re-render when a deadline crosses (so canVote flips live).
  const [, setDeadlineTick] = useState(0);

  const group = useMemo(
    () => (groups || []).find((entry) => entry.id === groupId) || null,
    [groups, groupId]
  );
  const isGroupMember = Boolean(group);
  const canManagePoll =
    Boolean(group && user?.uid) &&
    (group.creatorId === user.uid ||
      (group.memberManaged === true && Array.isArray(group.memberIds) && group.memberIds.includes(user.uid)));
  const archiveKey = useMemo(
    () => (groupId && pollId ? `basic:group:${groupId}:${pollId}` : null),
    [groupId, pollId]
  );
  const isArchived = Boolean(archiveKey && archivedPolls.includes(archiveKey));
  const participantIds = useMemo(() => {
    const ids = new Set(Array.isArray(group?.memberIds) ? group.memberIds : []);
    if (group?.creatorId) ids.add(group.creatorId);
    return Array.from(ids).filter(Boolean);
  }, [group?.creatorId, group?.memberIds]);
  const { profiles: participantProfilesById = {} } = useUserProfilesByIds(participantIds);
  const voteType = poll?.settings?.voteType || BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
  const isMultipleChoice = voteType === BASIC_POLL_VOTE_TYPES.MULTIPLE_CHOICE;
  const isRankedChoice = voteType === BASIC_POLL_VOTE_TYPES.RANKED_CHOICE;
  const allowMultiple = poll?.settings?.allowMultiple === true;
  const allowWriteIn = poll?.settings?.allowWriteIn === true;
  const maxSelections = Number.isFinite(poll?.settings?.maxSelections)
    ? Math.max(1, Number(poll.settings.maxSelections))
    : null;
  const isPollOpen = (poll?.status || BASIC_POLL_STATUSES.OPEN) === BASIC_POLL_STATUSES.OPEN;
  // Memoize on the numeric timestamp so the deadline effect below doesn't tear
  // down + re-create its setTimeout on every render (the resolver returns a fresh
  // Date each call → an unstable dep that thrashed the live-close timer).
  const deadlineMs = useMemo(() => {
    const parsed = resolveBasicPollDeadline(poll);
    return parsed ? parsed.getTime() : null;
  }, [poll]);
  const isDeadlinePassed = Boolean(deadlineMs && Date.now() >= deadlineMs);
  const canVote = isPollOpen && !isDeadlinePassed;
  const hasSubmitted = hasSubmittedVoteForPoll(poll, myVote);
  const voteCount = useMemo(
    () => (votes || []).filter((voteDoc) => hasSubmittedVoteForPoll(poll, voteDoc)).length,
    [poll, votes]
  );
  const participantUsers = useMemo(
    () =>
      participantIds
        .map((participantId) => {
          const profile = participantProfilesById?.[participantId] || {};
          return {
            id: participantId,
            email: profile?.email || null,
            avatar: profile?.photoURL || null,
            ...profile,
          };
        })
        .filter((entry) => entry.email),
    [participantIds, participantProfilesById]
  );
  const votedUsers = useMemo(() => {
    const entries = (votes || [])
      .filter((voteDoc) => hasSubmittedVoteForPoll(poll, voteDoc))
      .map((voteDoc) => {
        const profile = participantProfilesById?.[voteDoc.id] || {};
        return {
          id: voteDoc.id,
          email: profile?.email || voteDoc?.userEmail || null,
          avatar: voteDoc?.userAvatar || profile?.photoURL || null,
          ...profile,
        };
      })
      .filter((entry) => entry.email);
    const seen = new Set();
    return entries.filter((entry) => {
      if (!entry.id || seen.has(entry.id)) return false;
      seen.add(entry.id);
      return true;
    });
  }, [participantProfilesById, poll, votes]);
  const pendingUsers = useMemo(() => {
    const votedIdSet = new Set(votedUsers.map((entry) => entry.id).filter(Boolean));
    return participantUsers.filter((entry) => !votedIdSet.has(entry.id));
  }, [participantUsers, votedUsers]);
  // PR-5: guest-aware denominator from the poll's denormalized counters. The
  // displayed count is guest-INCLUSIVE (displayVoteCount); growing the
  // denominator by the guest-vote count keeps the summary from showing X>Y.
  // Falls back to the participant-only id count when no guest has voted (or the
  // counters aren't on the doc yet).
  const pollTally = getVoteTally(poll || {});
  const participantCountForSummary = pollTally.hasGuests
    ? pollTally.displayTotal
    : participantIds.length;
  const pollDiscord = poll?.discord || null;
  const discordStatusLabel = pollDiscord?.messageId
    ? "Posted in Discord"
    : group?.discord?.channelId
      ? "Discord linked"
      : "";
  const isPollCreator = Boolean(
    user?.uid && poll?.creatorId && String(poll.creatorId) === String(user.uid)
  );
  const canReadVoteDetails = useMemo(() => {
    if (!poll) return false;
    return canViewOtherVotesForUser({
      voteVisibility: resolveVoteVisibility(poll.voteVisibility),
      isCreator: isPollCreator,
      hasVoted: hasSubmittedVoteForPoll(poll, myVote),
      allParticipantsVoted: poll.votesAllSubmitted === true,
      isFinalized: (poll?.status || BASIC_POLL_STATUSES.OPEN) === BASIC_POLL_STATUSES.FINALIZED,
    });
  }, [isPollCreator, myVote, poll]);
  const voteIdentityDisplayMode = useMemo(() => {
    if (!poll) return "hidden";
    return getVoteIdentityDisplayMode({
      isCreator: isPollCreator,
      hideVoterIdentities: poll.hideVoterIdentities,
      voteAnonymization: poll.voteAnonymization,
      detailsRevealed: canReadVoteDetails,
    });
  }, [canReadVoteDetails, isPollCreator, poll]);
  const canReadVoterIdentities = voteIdentityDisplayMode !== "hidden";
  // Data-subscription predicate (NOT the per-target render contract): can this
  // viewer fetch the full peer votes collection? Same OR-gate as the Firestore
  // rules — when false we subscribe to only the viewer's own vote doc, so peer
  // voted/pending status is genuinely unknown.
  const canReadPeerVoteStatus = canReadVoteDetails || canReadVoterIdentities;
  const anonymousIdentityMap = useMemo(() => {
    if (voteIdentityDisplayMode !== "anonymous") return null;
    return buildAnonymousIdentityMap(
      [...participantUsers, ...votedUsers, ...pendingUsers],
      { scopeKey: `group:${groupId}:poll:${pollId}` }
    );
  }, [
    groupId,
    participantUsers,
    pendingUsers,
    pollId,
    voteIdentityDisplayMode,
    votedUsers,
  ]);
  const participantUsersForDisplay = useMemo(() => {
    if (voteIdentityDisplayMode !== "anonymous") return participantUsers;
    return anonymizeUsers(participantUsers, anonymousIdentityMap, { keyPrefix: "eligible", currentUser: user });
  }, [anonymousIdentityMap, participantUsers, user, voteIdentityDisplayMode]);
  const votedUsersForDisplay = useMemo(() => {
    if (voteIdentityDisplayMode !== "anonymous") return votedUsers;
    return anonymizeUsers(votedUsers, anonymousIdentityMap, { keyPrefix: "voted", currentUser: user });
  }, [anonymousIdentityMap, user, voteIdentityDisplayMode, votedUsers]);
  const pendingUsersForDisplay = useMemo(() => {
    if (voteIdentityDisplayMode !== "anonymous") return pendingUsers;
    return anonymizeUsers(pendingUsers, anonymousIdentityMap, { keyPrefix: "pending", currentUser: user });
  }, [anonymousIdentityMap, pendingUsers, user, voteIdentityDisplayMode]);
  const basicPollMissingNudgeUserIds = useMemo(() => {
    if (!poll || !user?.uid) return [];

    const submittedVoterIds = new Set(
      (votes || [])
        .filter((voteDoc) => hasSubmittedVoteForPoll(poll, voteDoc))
        .map((voteDoc) => String(voteDoc?.id || "").trim())
        .filter(Boolean)
    );

    return (participantIds || [])
      .map((participantId) => String(participantId || "").trim())
      .filter(Boolean)
      .filter((participantId) => participantId !== String(user.uid))
      .filter((participantId) => !submittedVoterIds.has(participantId));
  }, [participantIds, poll, user?.uid, votes]);
  const showBasicPollNudgeButton = Boolean(
    isPollCreator &&
      pollDiscord?.messageId &&
      isPollOpen &&
      basicPollMissingNudgeUserIds.length > 0
  );
  const cardBusy = submittingVote || clearingVote;
  const voteVisibilityHint = !canReadVoteDetails
    ? resolveVoteVisibility(poll?.voteVisibility) === "hidden_while_voting"
      ? "Vote details unlock after you submit your vote."
      : resolveVoteVisibility(poll?.voteVisibility) === "hidden_until_all_voted"
        ? "Vote details unlock once all participants have voted."
        : resolveVoteVisibility(poll?.voteVisibility) === "hidden_until_finalized"
          ? "Vote details unlock once this poll is finalized."
          : resolveVoteVisibility(poll?.voteVisibility) === "hidden"
            ? "Only the poll creator can view participant vote details."
            : null
    : null;

  useEffect(() => {
    if (!groupId || !pollId || !isGroupMember) {
      setPoll(null);
      setPollLoading(false);
      setPollError(null);
      return;
    }

    setPollLoading(true);
    setPollError(null);
    const unsubscribe = subscribeToBasicPoll(
      groupId,
      pollId,
      (nextPoll) => {
        setPoll(nextPoll);
        setPollLoading(false);
      },
      (error) => {
        setPoll(null);
        setPollError(error);
        setPollLoading(false);
      }
    );
    return () => unsubscribe();
  }, [groupId, pollId, isGroupMember, retryKey]);

  useEffect(() => {
    if (!groupId || !pollId || !isGroupMember) {
      setVotes([]);
      setVotesError(null);
      setVotesLoading(false);
      return;
    }

    if (!canReadPeerVoteStatus) {
      const ownVotes =
        user?.uid && hasSubmittedVoteForPoll(poll, myVote)
          ? [{ id: user.uid, ...(myVote || {}) }]
          : [];
      setVotes(ownVotes);
      setVotesError(null);
      setVotesLoading(false);
      return;
    }

    setVotesLoading(true);
    setVotesError(null);
    const unsubscribe = subscribeToBasicPollVotes(
      "group",
      groupId,
      pollId,
      (nextVotes) => {
        setVotes(nextVotes || []);
        setVotesError(null);
        setVotesLoading(false);
      },
      (error) => {
        // Surface the failure instead of silently reading as "0 voted". A
        // swallowed setVotes([]) here would corrupt any derived tally.
        setVotes([]);
        setVotesError(error || new Error("Failed to load votes."));
        setVotesLoading(false);
      }
    );
    return () => unsubscribe();
  }, [canReadPeerVoteStatus, groupId, isGroupMember, myVote, poll, pollId, user?.uid]);

  useEffect(() => {
    if (!groupId || !pollId || !isGroupMember || !user?.uid) {
      setMyVote(null);
      return;
    }

    const unsubscribe = subscribeToMyBasicPollVote(
      "group",
      groupId,
      pollId,
      user.uid,
      (nextVote) => setMyVote(nextVote || null),
      () => setMyVote(null)
    );
    return () => unsubscribe();
  }, [groupId, pollId, isGroupMember, user?.uid]);

  // Reset dirty state whenever we switch to a different poll.
  useEffect(() => {
    draftDirtyRef.current = false;
  }, [groupId, pollId]);

  useEffect(() => {
    if (!poll) return;
    // Don't clobber an in-progress edit when a realtime poll-metadata update
    // arrives (e.g. another participant's vote flips votesAllSubmitted). Only
    // sync from myVote when the draft is clean.
    if (draftDirtyRef.current) return;
    const sortedOptions = [...(Array.isArray(poll?.options) ? poll.options : [])].sort((left, right) => {
      const leftOrder = Number.isFinite(left?.order) ? left.order : Number.MAX_SAFE_INTEGER;
      const rightOrder = Number.isFinite(right?.order) ? right.order : Number.MAX_SAFE_INTEGER;
      return leftOrder - rightOrder;
    });
    const optionIds = new Set(sortedOptions.filter((option) => option?.id).map((option) => option.id));
    if (isMultipleChoice) {
      const selectedIds = normalizeVoteOptionIds(myVote).filter((id) => optionIds.has(id));
      setVoteDraft({
        optionIds: selectedIds,
        otherText: allowWriteIn ? String(myVote?.otherText || "") : "",
      });
      return;
    }
    if (isRankedChoice) {
      const rankings = normalizeVoteRankings(myVote).filter((id) => optionIds.has(id));
      setVoteDraft({ rankings, optionIds: [], otherText: "" });
      return;
    }
    setVoteDraft({});
  }, [allowWriteIn, isMultipleChoice, isRankedChoice, myVote, poll]);

  // Re-render when the deadline crosses so voting closes live (canVote flips).
  useEffect(() => {
    if (!deadlineMs) return undefined;
    const msUntil = deadlineMs - Date.now();
    if (msUntil <= 0) return undefined;
    // setTimeout delays are clamped to a 32-bit int (~24.8 days); cap so a
    // far-future deadline doesn't overflow and fire immediately.
    const MAX_TIMEOUT = 2147483647;
    const id = setTimeout(
      () => setDeadlineTick((n) => n + 1),
      Math.min(msUntil + 250, MAX_TIMEOUT)
    );
    return () => clearTimeout(id);
  }, [deadlineMs]);

  function markDraftDirty() {
    draftDirtyRef.current = true;
  }

  function setMultipleChoiceSelection(optionId) {
    setVoteError(null);
    markDraftDirty();
    setVoteDraft((previous) => {
      const { draft } = setMultipleChoiceOptionOnVoteDraft(previous, optionId, {
        allowMultiple,
        maxSelections,
      });
      return draft;
    });
  }

  function setOtherText(value) {
    setVoteError(null);
    markDraftDirty();
    setVoteDraft((previous) => setOtherTextOnVoteDraft(previous, value));
  }

  function addRankedOption(optionId) {
    setVoteError(null);
    markDraftDirty();
    setVoteDraft((previous) => addRankedOptionToVoteDraft(previous, optionId));
  }

  function moveRankedOption(optionId, direction) {
    markDraftDirty();
    setVoteDraft((previous) => moveRankedOptionInVoteDraft(previous, optionId, direction));
  }

  function removeRankedOption(optionId) {
    setVoteError(null);
    markDraftDirty();
    setVoteDraft((previous) => removeRankedOptionFromVoteDraft(previous, optionId));
  }

  async function submitVote() {
    if (!groupId || !pollId || !user?.uid) return;
    if (!canVote) {
      setVoteError(
        isDeadlinePassed
          ? "This poll's deadline has passed — voting is now closed."
          : "Voting is not open for this poll."
      );
      return;
    }
    setSubmittingVote(true);
    setVoteError(null);
    try {
      if (isMultipleChoice) {
        const optionIds = Array.isArray(voteDraft.optionIds) ? voteDraft.optionIds : [];
        const normalizedOtherText = String(voteDraft.otherText || "").trim();
        if (optionIds.length === 0 && normalizedOtherText.length === 0) {
          throw new Error("Select at least one option before submitting.");
        }
        if (!allowWriteIn && normalizedOtherText.length > 0) {
          throw new Error("Write-in voting is disabled for this poll.");
        }
        if (normalizedOtherText.length > MAX_WRITE_IN_LENGTH) {
          throw new Error(`Write-in options must be ${MAX_WRITE_IN_LENGTH} characters or fewer.`);
        }
        // maxSelections cap — a write-in counts as one selection.
        const totalSelections = optionIds.length + (normalizedOtherText.length > 0 ? 1 : 0);
        if (allowMultiple && maxSelections && totalSelections > maxSelections) {
          throw new Error(`You can select up to ${maxSelections} options (including write-ins).`);
        }
        if (!allowMultiple && totalSelections > 1) {
          throw new Error("This poll allows only one selection.");
        }
        await submitBasicPollVote("group", groupId, pollId, user.uid, {
          optionIds,
          otherText: normalizedOtherText,
          source: "web",
        });
      } else if (isRankedChoice) {
        const rankings = Array.isArray(voteDraft.rankings) ? voteDraft.rankings : [];
        if (rankings.length === 0) {
          throw new Error("Rank at least one option before submitting.");
        }
        await submitBasicPollVote("group", groupId, pollId, user.uid, {
          rankings,
          source: "web",
        });
      }
      // Allow the fresh myVote snapshot to re-sync the draft.
      draftDirtyRef.current = false;
    } catch (error) {
      setVoteError(error?.message || "Failed to submit vote.");
    } finally {
      setSubmittingVote(false);
    }
  }

  async function clearVote() {
    if (!groupId || !pollId || !user?.uid || !canVote) return;
    setClearingVote(true);
    setVoteError(null);
    try {
      await deleteBasicPollVote("group", groupId, pollId, user.uid);
      draftDirtyRef.current = false;
      setVoteDraft({});
    } catch (error) {
      setVoteError(error?.message || "Failed to clear vote.");
    } finally {
      setClearingVote(false);
    }
  }

  async function nudgeParticipants() {
    if (!groupId || !pollId) return;
    setNudgeSending(true);
    setVoteError(null);
    try {
      const result = await nudgeDiscordBasicPoll(groupId, pollId);
      const nudgedCount = Number(result?.nudgedCount || 0);
      const totalNonVoters = Number(result?.totalNonVoters || 0);
      if (nudgedCount < totalNonVoters) {
        toast.success(
          `Nudged ${nudgedCount} participant${nudgedCount === 1 ? "" : "s"} on Discord. ${totalNonVoters - nudgedCount} non-voter${totalNonVoters - nudgedCount === 1 ? " has" : "s have"} not linked Discord.`
        );
      } else {
        toast.success(
          `Nudged ${nudgedCount} participant${nudgedCount === 1 ? "" : "s"} on Discord!`
        );
      }
    } catch (error) {
      setVoteError(error?.message || "Failed to nudge participants.");
    } finally {
      setNudgeSending(false);
    }
  }

  async function toggleArchive() {
    if (!archiveKey) return;
    setHeaderActionBusy(true);
    try {
      if (isArchived) {
        await unarchivePoll(archiveKey);
      } else {
        await archivePoll(archiveKey);
      }
    } finally {
      setHeaderActionBusy(false);
    }
  }

  async function reopenPollAction() {
    if (!canManagePoll || !groupId || !pollId) return;
    setHeaderActionBusy(true);
    try {
      await reopenBasicPoll(groupId, pollId);
    } catch (error) {
      setVoteError(error?.message || "Failed to re-open poll.");
    } finally {
      setHeaderActionBusy(false);
    }
  }

  async function finalizePollAction() {
    if (!canManagePoll || !groupId || !pollId) return;
    setHeaderActionBusy(true);
    try {
      await finalizeBasicPoll(groupId, pollId);
    } catch (error) {
      setVoteError(error?.message || "Failed to finalize poll.");
    } finally {
      setHeaderActionBusy(false);
    }
  }

  function deletePollAction() {
    if (!canManagePoll || !groupId || !pollId) return;
    setDeleteConfirmOpen(true);
  }

  async function confirmDeletePollAction() {
    if (!canManagePoll || !groupId || !pollId) return;
    setHeaderActionBusy(true);
    try {
      await deleteBasicPoll(groupId, pollId, { useServer: true });
      setDeleteConfirmOpen(false);
      if (onPollDeleted) {
        onPollDeleted({ groupId, pollId });
      } else {
        onClose?.();
      }
    } catch (error) {
      setVoteError(error?.message || "Failed to delete poll.");
    } finally {
      setHeaderActionBusy(false);
    }
  }

  const showLoading = groupsLoading || pollLoading || votesLoading;
  const isAccessDenied = !groupsLoading && (!groupId || !isGroupMember || pollError?.code === "permission-denied");
  const hasGenericPollError = Boolean(pollError) && !isAccessDenied;
  // When the votes listener failed, suppress BOTH the derived tally AND the
  // vote-progress display — otherwise the now-empty `votes` would render a
  // misleading "0/N voted" + all-pending list that contradicts the error banner.
  const effectiveCanReadVoteDetails = votesError ? false : canReadVoteDetails;
  const effectiveCanReadPeerVoteStatus = votesError ? false : canReadPeerVoteStatus;
  // statusKnown: the per-person voted/pending SPLIT is known only once the gated
  // votes snapshot resolved cleanly — viewer entitled AND not loading AND no
  // error. When false the summary renders a neutral split ("—") rather than a
  // definitive "0/N voted". The aggregate Layer-A count still shows.
  const statusKnown = effectiveCanReadPeerVoteStatus && !votesLoading && !votesError;
  // Layer A count must stay authoritative even when the viewer can't read peer
  // status (then `votes` holds only the viewer's own doc → voteCount is 0/1).
  // Fall back to the denormalized poll counter the trigger keeps current.
  // PR-5: submittedVoteCount is guest-INCLUSIVE; participantCountForSummary is
  // now the guest-AWARE denominator (grows by the guest-vote count via
  // getVoteTally above), so the displayed count no longer skews to X>Y.
  const denormalizedVoteCount = Number.isFinite(poll?.submittedVoteCount)
    ? poll.submittedVoteCount
    : null;
  const displayVoteCount = effectiveCanReadPeerVoteStatus
    ? voteCount
    : denormalizedVoteCount ?? voteCount;

  return (
    <>
      <Dialog open onOpenChange={(open) => { if (!open) onClose?.(); }}>
        <DialogContent
          showCloseButton={false}
          aria-describedby={undefined}
          className="max-w-4xl p-0"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                General poll
              </p>
              <DialogTitle
                className="truncate text-base font-semibold text-card-foreground"
                title={poll?.title || "Loading poll..."}
              >
                {poll?.title || "Loading poll..."}
              </DialogTitle>
              {group?.name ? (
                <p className="text-xs text-muted-foreground">Questing group: {group.name}</p>
              ) : null}
              <PollDiscordMetaRow
                statusLabel={discordStatusLabel}
                messageUrl={pollDiscord?.messageUrl || ""}
                pendingSync={pollDiscord?.pendingSync === true}
                className="mt-2"
              >
                {showBasicPollNudgeButton ? (
                  <PollNudgeButton
                    onClick={nudgeParticipants}
                    sending={nudgeSending}
                    lastNudgeAt={pollDiscord?.nudgeLastSentAt}
                  />
                ) : null}
              </PollDiscordMetaRow>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {canManagePoll &&
              (poll?.status || BASIC_POLL_STATUSES.OPEN) === BASIC_POLL_STATUSES.FINALIZED ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={reopenPollAction}
                  disabled={headerActionBusy}
                  className="min-h-10 rounded-full px-3 py-1 text-xs"
                >
                  Re-open
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={toggleArchive}
                disabled={headerActionBusy}
                className="min-h-10 gap-1 rounded-full px-3 py-1 text-xs"
              >
                {isArchived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                {isArchived ? "Unarchive" : "Archive"}
              </Button>
              {canManagePoll ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={headerActionBusy}
                      className="min-h-10 min-w-10 rounded-full p-2"
                      aria-label="General poll actions"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    {canManagePoll &&
                    (poll?.status || BASIC_POLL_STATUSES.OPEN) === BASIC_POLL_STATUSES.OPEN ? (
                      <DropdownMenuItem onClick={finalizePollAction} disabled={headerActionBusy}>
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Finalize
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => {
                        // Attach vote info (mirroring buildDashboardPollSummary) so
                        // the edit-after-votes structural lock in CreateGroupPollModal
                        // activates from this entry point too — not just the dashboard
                        // card edit path. The backstop in updateBasicPoll still guards
                        // regardless, but this makes the UX lock consistent.
                        const submittedVoterIds = (votes || [])
                          .filter((voteDoc) => hasSubmittedVoteForPoll(poll, voteDoc))
                          .map((voteDoc) => voteDoc.id)
                          .filter(Boolean);
                        onEditPoll?.({
                          groupId,
                          pollId,
                          poll: {
                            ...poll,
                            votedCount: voteCount,
                            voterIds: Array.from(new Set(submittedVoterIds)),
                          },
                        });
                      }}
                      disabled={headerActionBusy}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    {canManagePoll ? (
                      <DropdownMenuItem
                        onClick={deletePollAction}
                        disabled={headerActionBusy}
                        className="text-rose-600 dark:text-rose-400"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                onClick={() => onClose?.()}
                className="min-h-10 min-w-10 rounded-full p-2 text-muted-foreground hover:text-card-foreground"
                aria-label="Close general poll modal"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="max-h-[80vh] overflow-y-auto p-5">
            {showLoading ? (
              <LoadingState message="Loading group poll..." />
            ) : isAccessDenied ? (
              <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">
                You do not have access to this group poll.
              </p>
            ) : hasGenericPollError ? (
              <ErrorState
                title="Couldn't load this poll"
                message={pollError?.message || "We couldn't load this poll right now. Check your connection and try again."}
                onRetry={() => setRetryKey((n) => n + 1)}
                retryLabel="Try again"
              />
            ) : !poll ? (
              <p className="text-sm text-muted-foreground">This group poll was not found.</p>
            ) : (
              <div className="space-y-4">
                {votesError ? (
                  <ErrorState
                    title="Couldn't load vote results"
                    message="Live results and vote progress couldn't be loaded. You can still cast your own vote."
                  />
                ) : null}
                <BasicPollVotingCard
                  poll={poll}
                  participantCount={participantCountForSummary}
                  voteCount={displayVoteCount}
                  hasSubmitted={hasSubmitted}
                  myVote={myVote}
                  draft={voteDraft}
                  canVote={canVote}
                  cardBusy={cardBusy}
                  voteError={voteError}
                  onMoveRankedOption={moveRankedOption}
                  onAddRankedOption={addRankedOption}
                  onRemoveRankedOption={removeRankedOption}
                  onSelectOption={setMultipleChoiceSelection}
                  onChangeOtherText={setOtherText}
                  onSubmitVote={submitVote}
                  onClearVote={clearVote}
                  eligibleUsers={participantUsersForDisplay}
                  votedUsers={votedUsersForDisplay}
                  pendingUsers={pendingUsersForDisplay}
                  showVoteProgress={effectiveCanReadPeerVoteStatus}
                  statusKnown={statusKnown}
                  showVoterIdentities={effectiveCanReadPeerVoteStatus && canReadVoterIdentities}
                  voteIdentityDisplayMode={voteIdentityDisplayMode}
                  voteVisibilityHint={voteVisibilityHint}
                  votes={votes}
                  canReadVoteDetails={effectiveCanReadVoteDetails}
                  canReadVoteProgress={effectiveCanReadPeerVoteStatus}
                  onViewOptionNote={(pollTitle, option) =>
                    setOptionNoteViewer({
                      pollTitle: String(pollTitle || "General poll"),
                      optionLabel: String(option?.label || "Option"),
                      note: String(option?.note || ""),
                    })
                  }
                />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <PollOptionNoteDialog
        noteViewer={optionNoteViewer}
        onClose={() => setOptionNoteViewer(null)}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={`Delete "${poll?.title || "this poll"}"?`}
        description="This will remove all votes and cannot be undone."
        confirmLabel="Delete poll"
        confirming={headerActionBusy}
        onConfirm={confirmDeletePollAction}
        variant="destructive"
      />
    </>
  );
}
